import React, { useState, useEffect, useContext } from "react";
import {
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid2,
  Tooltip,
  CardHeader,
  IconButton,
  Box,
} from "@mui/material";
import { Mic, Stop, Save, AutoFixHigh, ContentCopy } from "@mui/icons-material";
import { getServiceDetails } from "../API/LinkedServicesApi";
import {
  addclinicalNote,
  updateClinicalNote,
  getClinicalNote,
  getClinicalNotesForService,
} from "../API/ClinicalNoteApi";
import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";
import dayjs from "dayjs";
import { formatAge, showLoadingImage } from "../Utility/Utility";
import MenuBar from "../common/MenuBar";
import "../common/styles/CommonStyles.css";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import NotesTextField from "./NotesTextField"; // Import the new component

const GeneralNote = ({ serviceId, onSaveNote, noteId }) => {
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);

  const [text, setText] = useState("");
  const [rephrasedText, setRephrasedText] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [showRephraseBox, setShowRephraseBox] = useState(false);
  const [service, setService] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentNoteId, setCurrentNoteId] = useState(noteId);
  const [isSaving, setIsSaving] = useState(false);
  const [noteStatus, setNoteStatus] = useState("Draft");

  const [currentService, setCurrentService] = useState();
  const [showSpinner, setShowSpinner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const showLoading = isLoading || showSpinner;
  const displayText = (value, fallback = "Not available") => {
    return value !== undefined && value !== null ? value : fallback;
  };

  let recognition;
  if ("webkitSpeechRecognition" in window) {
    recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      const lastTranscript =
        event.results[event.results.length - 1][0].transcript;
      setText((prevText) => prevText + " " + lastTranscript);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      showSnackbar("Speech recognition error: " + event.error, "error");
    };
  }

  useEffect(() => {
    const loadService = async () => {
      if (auth != null && serviceId != null) {
        try {
          const result = await getServiceDetails(auth, serviceId);
          if (result?.isSuccess) {
            setService(result?.data);
            setCurrentService(result?.data);
          } else {
            showSnackbar(
              result?.data || "Error fetching patient details",
              "error"
            );
          }
        } catch (error) {
          showSnackbar(
            "Error loading service details: " + error.message,
            "error"
          );
        }
      }
    };

    loadService();
  }, [serviceId, auth, showSnackbar]);

  useEffect(() => {
    const loadNote = async () => {
      if (auth != null && currentNoteId != null) {
        setIsSaving(true);
        try {
          const result = await getClinicalNote(auth, currentNoteId);
          if (result.isSuccess) {
            const noteData = JSON.parse(result?.data?.noteJson);
            setText(noteData.text || noteData.generalNotes || "");
            setNoteStatus(result?.data?.status || "Draft");
          } else {
            await loadDraftNote();
          }
        } catch (error) {
          showSnackbar("Error loading note: " + error.message, "error");
          await loadDraftNote();
        } finally {
          setIsSaving(false);
        }
      } else if (auth != null && serviceId != null) {
        await loadDraftNote();
      }
    };

    const loadDraftNote = async () => {
      if (auth != null && serviceId != null) {
        setIsSaving(true);
        try {
          const notesResult = await getClinicalNotesForService(auth, serviceId);
          if (notesResult.isSuccess) {
            const draftNote = notesResult.data.find(
              (note) => note.status === "Draft"
            );
            if (draftNote) {
              try {
                const noteData = draftNote.noteJson
                  ? JSON.parse(draftNote.noteJson)
                  : {};
                setText(noteData.text || noteData.generalNotes || "");
                setCurrentNoteId(draftNote.noteId);
                setNoteStatus(draftNote.status || "Draft");
              } catch (parseError) {
                console.error("Error parsing draft note JSON:", parseError);
                setText("");
              }
            }
          } else {
            showSnackbar(
              notesResult.data?.message || "Error fetching note",
              "error"
            );
          }
        } catch (error) {
          console.error("Error loading note:", error);
          showSnackbar("Error loading note: " + error.message, "error");
        } finally {
          setIsSaving(false);
        }
      }
    };

    loadNote();
  }, [currentNoteId, auth, serviceId, showSnackbar]);

  const startListening = () => {
    if (recognition) {
      setIsListening(true);
      recognition.start();
    } else {
      showSnackbar("Speech recognition not supported in this browser", "error");
    }
  };

  const stopListening = () => {
    if (recognition) {
      setIsListening(false);
      recognition.stop();
    }
  };

  const handleSaveNote = async (status = "Draft") => {
    setIsSaving(true);

    const noteData = {
      generalNotes: text,
      text: text,
    };

    try {
      let result;
      if (!currentNoteId) {
        result = await addclinicalNote(auth, noteData, serviceId, "general");
        if (result.isSuccess && result.data) {
          setCurrentNoteId(result.data);
        }
      } else {
        result = await updateClinicalNote(auth, noteData, currentNoteId);
      }

      if (result.isSuccess) {
        showSnackbar("Note saved successfully", "success");
        setNoteStatus(status);

        if (onSaveNote) onSaveNote(serviceId, currentNoteId, status);
      } else {
        showSnackbar("Failed to save note", "error");
      }
    } catch (error) {
      showSnackbar("Error saving note: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRephrase = () => {
    if (!text.trim()) {
      showSnackbar("Please enter some text to rephrase", "warning");
      return;
    }

    if (showRephraseBox) {
      setShowRephraseBox(false);
    } else {
      const rephrased = `Rephrased: ${text}`;
      setRephrasedText(rephrased);
      setShowRephraseBox(true);
    }
  };
  let clinicalServiceCode = displayText(currentService?.serviceCode);
  let serviceName = displayText(currentService?.serviceName);

  let finalserviceName = clinicalServiceCode +" "+ serviceName;

  const handleAppend = () => {
    setText((prevText) => prevText + " " + rephrasedText);
    setShowRephraseBox(false);
  };

  const handleReplace = () => {
    setText(rephrasedText);
    setShowRephraseBox(false);
  };

  const handleCopyRephrasedText = () => {
    navigator.clipboard.writeText(rephrasedText);
    showSnackbar("Rephrased text copied to clipboard!", "success");
  };

  // Handle text change from NotesTextField component
  const handleTextChange = (newText) => {
    setText(newText);
  };

  return (
    <Grid2 container sx={{ width: "100%" }}>
      {/* Loading Overlay */}
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301,
          }}
        >
          {showLoadingImage()}
        </div>
      )}

      {/* Save/Download Button Row */}
      <Grid2 size={12}>
        {noteStatus === "Draft" && (
          <MenuBar showSave={true} onSave={() => handleSaveNote("Draft")} />
        )}
      </Grid2>

      <Grid2
        item
        size={{ xs: 12, md: 8, lg: 6 }}
        sx={{ ml: 1, mt: 1, maxHeight: "70vh", overflowY: "auto" }}
      >
        <Card
          className="outerCard"
          variant="outlined"
          sx={{
            width: "100%",
            p: { xs: 2, md: 4 },
          }}
        >
          {/* Patient Details */}
          <Grid2 size={12}>
            <Card
              sx={{
                padding: "16px",
                pointerEvents: showLoading ? "none" : "auto",
                opacity: showLoading ? 0.6 : 1,
              }}
              variant="outlined"
            >
              <Grid2 size={12}>
                <Grid2 container size={12}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Patient Name"
                      value={
                        currentService?.patientPrefix +
                        " " +
                        currentService?.patientFirstName +
                        " " +
                        currentService?.patientLastName
                      }
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Age and Gender"
                      value={
                        formatAge(currentService?.patientAgeInYears) +
                        " and " +
                        currentService?.patientGender
                      }
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="MRN"
                      value={displayText(currentService?.patientMRN)}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container size={12}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Visit Id"
                      value={displayText(currentService?.visitDisplayId)}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Visit Type"
                      value={displayText(currentService?.visitType)}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Visit Date Time"
                      value={convertUTCToTimeZoneDateTimeString(
                        new Date().toISOString(),
                        Intl.DateTimeFormat().resolvedOptions().timeZone
                      )}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container size={12}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Service Id"
                      value={displayText(currentService?.displayId)}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Service Name"
                      value={finalserviceName}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Service Date Time"
                      value={convertUTCToTimeZoneDateTimeString(
                        new Date().toISOString(),
                        Intl.DateTimeFormat().resolvedOptions().timeZone
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>

          {/* Clinical Note Content - Using the new NotesTextField component */}
          <Grid2 mb={3}>
            <Typography fontWeight="bold" gutterBottom>
              Clinical Note
            </Typography>

            <NotesTextField
              value={text}
              onChange={handleTextChange}
              label="Enter your notes"
              disabled={isSaving}
              rows={8}
              noteStatus={noteStatus}
            />
          </Grid2>

          {/* Rephrased Section */}
          {showRephraseBox && (
            <Grid2 mb={3}>
              <Card
                variant="outlined"
                sx={{ background: "#f9f9f9", borderRadius: 2 }}
              >
                <CardHeader
                  subheader="Rephrased Text"
                  sx={{ padding: "8px 16px" }}
                />
                <CardContent sx={{ padding: "8px" }}>
                  <TextField
                    multiline
                    rows={8}
                    value={rephrasedText}
                    variant="outlined"
                    fullWidth
                    disabled
                    sx={{ mb: 1, background: "#fff", borderRadius: 1 }}
                  />
                  <Grid2 display="flex" gap={1} justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAppend}
                      disabled={noteStatus === "Final" || isSaving}
                      sx={{ textTransform: "none" }}
                    >
                      Append
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleReplace}
                      disabled={noteStatus === "Final" || isSaving}
                      sx={{ textTransform: "none" }}
                    >
                      Replace
                    </Button>
                    <Tooltip title="Copy rephrased text" arrow>
                      <IconButton
                        color="primary"
                        onClick={handleCopyRephrasedText}
                        size="small"
                      >
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </Grid2>
                </CardContent>
              </Card>
            </Grid2>
          )}
        </Card>
      </Grid2>
    </Grid2>
  );
};

export default GeneralNote;